import React, { useEffect, useMemo, useState } from 'react';
import { buildDailyStatsFromTrades, buildMonthlyRows } from '../utils/vectorCapitalScenario';
import { MonthlyReturnsInfoButton } from './MonthlyReturnsInfoModal';
import { monthlyMethodForScenario } from '../utils/vectorMonthlyReturnMethod';
import { IconChevronDown } from './Icons';

export { buildMonthlyRows };

/**
 * Fyntrak-style "Calendar Profits" suite for the Vector Capital scenario page:
 * - VectorCapitalCalendar (default): daily P&L month grid + month stats +
 *   weekly Total/Average P&L breakdowns.
 * - VectorCapitalMonthlyReturns: month-by-month P&L / return per strategy.
 * - VectorCapitalTradesList: simulated daily results table ("list of trades").
 *
 * All are derived from each scenario's simulated daily equity series
 * (value[i] - value[i-1]). Benchmark comparison charts extend through the
 * benchmark's latest date; scenario lines forward-fill after their last point.
 *
 * Shared prop shape:
 * - scenarios: [{ id, label, series, trades?, positions?, symbol? }]
 */

const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_LABELS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatPnl(value) {
  const abs = Math.abs(value);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: abs < 100 ? 2 : 0,
  }).format(abs);
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return formatted;
}

/** List of Trades Net P&L — always two decimals (Fyntrak parity). */
function formatTradePnl(value) {
  if (value === null || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(abs);
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return formatted;
}

function formatPct(value) {
  if (value === null || !Number.isFinite(value)) return '';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function pnlToneClass(value) {
  if (value > 0) return 'pos';
  if (value < 0) return 'neg';
  return '';
}

/** 'YYYY-MM' → 'Apr 2026'. */
function monthKeyLabel(monthKey) {
  const m = Number(String(monthKey).slice(5, 7)) - 1;
  return `${MONTH_LABELS_SHORT[m] || ''} ${String(monthKey).slice(0, 4)}`;
}

/** 'YYYY-MM-DD' → 'M/D/YYYY'. */
function tradeDateLabel(date) {
  const y = String(date).slice(0, 4);
  const m = Number(String(date).slice(5, 7));
  const d = Number(String(date).slice(8, 10));
  return `${m}/${d}/${y}`;
}

/** date ('YYYY-MM-DD') → { pnl, pct, entry, exit } from a scenario's daily
 *  equity series (entry/exit are the simulated account values bounding the day).
 *  Uses the history-windowed series rebased to the capital base at the floor date. */
function buildDailyPnlByDate(series) {
  const out = new Map();
  const rows = Array.isArray(series) ? series : [];
  for (let i = 1; i < rows.length; i += 1) {
    const prev = Number(rows[i - 1]?.normalized);
    const cur = Number(rows[i]?.normalized);
    const date = rows[i]?.date;
    if (!date || !Number.isFinite(prev) || !Number.isFinite(cur)) continue;
    out.set(date, {
      pnl: cur - prev,
      pct: prev > 0 ? ((cur / prev) - 1) * 100 : null,
      entry: prev,
      exit: cur,
    });
  }
  return out;
}

/** Calendar / trades daily values — prefer the history-windowed chart series. */
function activeSeriesForDaily(active) {
  if (Array.isArray(active?.seriesWindowed) && active.seriesWindowed.length > 1) {
    return active.seriesWindowed;
  }
  if (Array.isArray(active?.calendarSeries) && active.calendarSeries.length > 1) {
    return active.calendarSeries;
  }
  return active?.series;
}

/**
 * Daily calendar stats from trade journal, equity series, or precomputed map.
 * Uses constant-size (fixed-notional) P&L — raw per-contract $ with real volumes
 * when a trade journal is available; otherwise day-over-day $ from the series.
 */
function buildDailyCalendarStats(active) {
  // Some scenarios carry trades that are a DISPLAY LIST, not the arithmetic
  // behind their days. The OG Equity Portfolio is the case: its daily values
  // mirror Fyntrak's exported curve, while its trades are the per-leg rows from
  // Strategy Data — the same positions, but RiskLo's own reconstruction, which
  // lands ~70% higher when compounded. Deriving the calendar from the trades
  // there would silently swap one for the other.
  //
  // The precedence used to be positional (trades first, always). Scenarios that
  // know their daily stats are authoritative now say so.
  if (active?.dailyStatsAuthoritative
    && active?.dailyStatsByDate instanceof Map && active.dailyStatsByDate.size) {
    return active.dailyStatsByDate;
  }

  const tradesStats = Array.isArray(active?.trades) && active.trades.length
    ? buildDailyStatsFromTrades(active.trades)
    : null;
  if (tradesStats) return tradesStats;

  if (active?.dailyStatsByDate instanceof Map && active.dailyStatsByDate.size) {
    return active.dailyStatsByDate;
  }

  const fromSeries = buildDailyPnlByDate(activeSeriesForDaily(active));
  const enriched = new Map();
  fromSeries.forEach((data, date) => {
    if (!data.pnl) return;
    enriched.set(date, {
      ...data,
      volume: active?.positions || 1,
      trades: 1,
      wins: data.pnl > 0 ? 1 : 0,
      losses: data.pnl < 0 ? 1 : 0,
    });
  });
  return enriched;
}

function formatAccountValue(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(value);
}

/** Months ('YYYY-MM') present in a daily-P&L map, sorted ascending. */
function monthKeysOf(dailyPnlByDate) {
  const set = new Set();
  dailyPnlByDate.forEach((_, date) => set.add(date.slice(0, 7)));
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Keeps a select-driven "active strategy" valid as the upstream list changes. */
function useActiveScenario(scenarios) {
  const [activeId, setActiveId] = useState(scenarios[0]?.id || '');
  useEffect(() => {
    if (!scenarios.length) return;
    if (!scenarios.some((s) => s.id === activeId)) {
      setActiveId(scenarios[0].id);
    }
  }, [scenarios, activeId]);
  const active = scenarios.find((s) => s.id === activeId) || scenarios[0];
  return [active, setActiveId];
}

/** Month picker state pinned to the months that actually have data. */
function useMonthKey(monthKeys) {
  const [monthKey, setMonthKey] = useState(null);
  useEffect(() => {
    setMonthKey((prev) => {
      if (!monthKeys.length) return null;
      return prev && monthKeys.includes(prev) ? prev : monthKeys[monthKeys.length - 1];
    });
  }, [monthKeys]);
  return [monthKey, setMonthKey];
}

function MonthNav({ monthKeys, monthKey, onChange }) {
  const monthIndex = monthKey ? monthKeys.indexOf(monthKey) : -1;
  const year = monthKey ? Number(monthKey.slice(0, 4)) : null;
  const month = monthKey ? Number(monthKey.slice(5, 7)) - 1 : null;
  const years = Array.from(new Set(monthKeys.map((k) => k.slice(0, 4)))).sort();
  const monthsInYear = year === null
    ? []
    : monthKeys.filter((k) => k.startsWith(`${year}-`)).map((k) => Number(k.slice(5, 7)) - 1);

  if (!monthKey) return null;

  return (
    <div className="vcc-month-nav">
      <button
        type="button"
        className="vcc-nav-btn"
        onClick={() => monthIndex > 0 && onChange(monthKeys[monthIndex - 1])}
        disabled={monthIndex <= 0}
        aria-label="Previous month"
      >
        ‹
      </button>
      <select
        className="vcc-select"
        value={month}
        onChange={(e) => onChange(`${year}-${String(Number(e.target.value) + 1).padStart(2, '0')}`)}
        aria-label="Month"
      >
        {monthsInYear.map((m) => (
          <option key={m} value={m}>{MONTH_LABELS[m]}</option>
        ))}
      </select>
      <select
        className="vcc-select"
        value={year}
        onChange={(e) => {
          const inYear = monthKeys.filter((k) => k.startsWith(`${e.target.value}-`));
          if (inYear.length) onChange(inYear[inYear.length - 1]);
        }}
        aria-label="Year"
      >
        {years.map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
      <button
        type="button"
        className="vcc-nav-btn"
        onClick={() => monthIndex < monthKeys.length - 1 && onChange(monthKeys[monthIndex + 1])}
        disabled={monthIndex >= monthKeys.length - 1}
        aria-label="Next month"
      >
        ›
      </button>
    </div>
  );
}

function ScenarioSelect({ scenarios, active, onChange }) {
  if (scenarios.length > 1) {
    return (
      <select
        className="vcc-select vcc-scenario-select"
        value={active.id}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Strategy shown"
      >
        {scenarios.map((s) => (
          <option key={s.id} value={s.id}>{s.label}</option>
        ))}
      </select>
    );
  }
  return <span className="vcc-single-label">{active.label}</span>;
}

function VectorCapitalCalendar({ scenarios }) {
  const [active, setActiveId] = useActiveScenario(scenarios);

  const fixedBase = useMemo(() => {
    const first = Number(active?.series?.[0]?.normalized);
    return Number.isFinite(first) && first > 0 ? first : 100000;
  }, [active]);
  const dailyStatsByDate = useMemo(() => {
    const rawDailyStats = buildDailyCalendarStats(active);
    // Kairos-style compounding curves need day % × base for constant-size P&L.
    // Fixed-notional strategies already carry constant-size $ in series increments.
    if (!active?.compoundMonthly) return rawDailyStats;
    const out = new Map();
    rawDailyStats.forEach((data, date) => {
      const pct = Number(data.pct);
      out.set(date, Number.isFinite(pct)
        ? { ...data, pnl: (pct / 100) * fixedBase }
        : data);
    });
    return out;
  }, [active, fixedBase]);
  const monthKeys = useMemo(() => monthKeysOf(dailyStatsByDate), [dailyStatsByDate]);
  const [monthKey, setMonthKey] = useMonthKey(monthKeys);

  const year = monthKey ? Number(monthKey.slice(0, 4)) : null;
  const month = monthKey ? Number(monthKey.slice(5, 7)) - 1 : null; // 0-based

  // Calendar grid cells: leading blanks for the first weekday, then day numbers.
  const cells = useMemo(() => {
    if (year === null || month === null) return [];
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const out = [];
    for (let i = 0; i < firstWeekday; i += 1) out.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) out.push(d);
    return out;
  }, [year, month]);

  const monthStats = useMemo(() => {
    if (!monthKey) return null;
    let days = 0;
    let total = 0;
    let wins = 0;
    let losses = 0;
    let best = null;
    let worst = null;
    dailyStatsByDate.forEach((data, date) => {
      if (!date.startsWith(`${monthKey}-`)) return;
      days += 1;
      total += data.pnl;
      if (data.pnl > 0) wins += 1;
      if (data.pnl < 0) losses += 1;
      if (best === null || data.pnl > best) best = data.pnl;
      if (worst === null || data.pnl < worst) worst = data.pnl;
    });
    return {
      days,
      total,
      avg: days > 0 ? total / days : 0,
      wins,
      losses,
      best,
      worst,
    };
  }, [dailyStatsByDate, monthKey]);

  // Weekly Total / Average P&L for the visible month (week = calendar row).
  const weeklyStats = useMemo(() => {
    if (!monthKey || year === null || month === null) return [];
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const weekCount = Math.ceil((firstWeekday + daysInMonth) / 7);
    const weeks = Array.from({ length: weekCount }, () => ({ total: 0, days: 0 }));
    dailyStatsByDate.forEach((data, date) => {
      if (!date.startsWith(`${monthKey}-`)) return;
      const dayOfMonth = Number(date.slice(8, 10));
      const weekIdx = Math.floor((dayOfMonth + firstWeekday - 1) / 7);
      if (weeks[weekIdx]) {
        weeks[weekIdx].total += data.pnl;
        weeks[weekIdx].days += 1;
      }
    });
    return weeks;
  }, [dailyStatsByDate, monthKey, year, month]);

  const headerTitle = (
    <div>
      <span className="vector-capital-panel-eyebrow">Calendar</span>
      <h3>Calendar Profits</h3>
    </div>
  );

  if (!active || !monthKey) {
    return (
      <>
        <div className="vector-capital-panel-header">{headerTitle}</div>
        <div className="vcc-empty">
          No daily strategy data in the selected range yet — pick a strategy preset or widen the date range.
        </div>
      </>
    );
  }

  const getDayData = (day) => dailyStatsByDate.get(`${monthKey}-${String(day).padStart(2, '0')}`) || null;

  return (
    <div className="vcc">
      <div className="vector-capital-panel-header vcc-header">
        {headerTitle}
        <div className="vcc-toolbar">
          <ScenarioSelect scenarios={scenarios} active={active} onChange={setActiveId} />
          <MonthNav monthKeys={monthKeys} monthKey={monthKey} onChange={setMonthKey} />
        </div>
      </div>

      <div className="vcc-grid-scroll">
        <div className="vcc-grid">
          {DAY_HEADERS.map((d) => (
            <div key={d} className="vcc-dow">{d}</div>
          ))}
          {cells.map((day, idx) => {
            if (!day) return <div key={`blank-${idx}`} className="vcc-cell vcc-cell--blank" />;
            const data = getDayData(day);
            const weekday = new Date(year, month, day).getDay();
            const isWeekend = weekday === 0 || weekday === 6;
            const tone = data ? (data.pnl > 0 ? 'pos' : data.pnl < 0 ? 'neg' : 'flat') : (isWeekend ? 'weekend' : 'idle');
            return (
              <div key={day} className={`vcc-cell vcc-cell--${tone}`}>
                <span className="vcc-cell-day">{day}</span>
                {data && (
                  <span className="vcc-cell-body">
                    <strong className="vcc-cell-pnl">{formatPnl(data.pnl)}</strong>
                    {data.trades > 0 && (
                      <span className="vcc-cell-meta">
                        <span className="vcc-cell-meta-row">
                          <span>Vol: {data.volume}</span>
                          <span>Trades: {data.trades}</span>
                        </span>
                        <span className="vcc-cell-meta-row vcc-cell-meta-center">
                          Win: {data.wins} Loss: {data.losses}
                        </span>
                      </span>
                    )}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {monthStats && (
        <div className="vcc-footer">
          <div className="vcc-stats">
            <div className="vcc-stat">
              <span>Days</span>
              <strong className="vcc-stat-value">{monthStats.days}</strong>
            </div>
            <div className="vcc-stat">
              <span>Total P&amp;L</span>
              <strong className={`vcc-stat-value ${pnlToneClass(monthStats.total)}`}>
                {formatPnl(monthStats.total)}
              </strong>
            </div>
            <div className="vcc-stat">
              <span>Avg / Day</span>
              <strong className={`vcc-stat-value ${pnlToneClass(monthStats.avg)}`}>{formatPnl(monthStats.avg)}</strong>
            </div>
            <div className="vcc-stat">
              <span>Win / Loss Days</span>
              <strong className="vcc-stat-value vcc-stat-value--winloss">
                <span className="pos">{monthStats.wins}</span>
                <span className="vcc-stat-divider">/</span>
                <span className="neg">{monthStats.losses}</span>
              </strong>
            </div>
            <div className="vcc-stat">
              <span>Best Day</span>
              <strong className="vcc-stat-value pos">{monthStats.best !== null ? formatPnl(monthStats.best) : '—'}</strong>
            </div>
            <div className="vcc-stat">
              <span>Worst Day</span>
              <strong className="vcc-stat-value neg">{monthStats.worst !== null ? formatPnl(monthStats.worst) : '—'}</strong>
            </div>
          </div>

          <div className="vcc-weekly">
            <div className="vcc-weekly-col">
              <div className="vcc-weekly-head">Total P&amp;L</div>
              {weeklyStats.map((w, i) => (
                <div key={`total-${i}`} className="vcc-weekly-row">
                  <span>Week {i + 1}</span>
                  <strong className={pnlToneClass(w.total)}>{w.days ? formatPnl(w.total) : '$0'}</strong>
                </div>
              ))}
            </div>
            <div className="vcc-weekly-col">
              <div className="vcc-weekly-head">Average P&amp;L</div>
              {weeklyStats.map((w, i) => (
                <div key={`avg-${i}`} className="vcc-weekly-row">
                  <span>Week {i + 1}</span>
                  <strong className={w.days ? pnlToneClass(w.total / w.days) : ''}>
                    {w.days ? formatPnl(w.total / w.days) : 'N/A'}
                  </strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {active.calendarFootnote ? (
        <p className="vector-capital-chart-footnote vcc-calendar-footnote">{active.calendarFootnote}</p>
      ) : null}
    </div>
  );
}

/** Fyntrak-style "Monthly Returns": pick a scenario + year, compare the
 *  scenario's monthly % returns against every loaded benchmark, Jan–Dec. */
export function VectorCapitalMonthlyReturns({
  scenarios,
  benchmarks = [],
  capitalBase = 100000,
  resolveMonthlyFixedBase,
  monthlyReturnMethods,
  registryRowForScenarioId,
}) {
  const [expanded, setExpanded] = useState(true);
  const [active, setActiveId] = useActiveScenario(scenarios);
  const scenarioFixedBase = typeof resolveMonthlyFixedBase === 'function'
    ? resolveMonthlyFixedBase(active?.id)
    : capitalBase;
  const scenarioMonthlyMethod = monthlyMethodForScenario(
    active?.id,
    typeof registryRowForScenarioId === 'function' ? registryRowForScenarioId(active?.id) : null,
    monthlyReturnMethods,
  );

  const scenarioMonthly = useMemo(
    () => new Map(
      buildMonthlyRows(active?.series, {
        fixedBaseCapital: scenarioFixedBase,
      }).map((r) => [r.key, r]),
    ),
    [active, scenarioFixedBase],
  );
  const benchmarkMonthly = useMemo(
    () => benchmarks.map((b) => ({
      ...b,
      rows: new Map(buildMonthlyRows(b.series).map((r) => [r.key, r])),
    })),
    [benchmarks],
  );

  // Years with any data (scenario or benchmark); default to the latest.
  const years = useMemo(() => {
    const set = new Set();
    scenarioMonthly.forEach((_, key) => set.add(key.slice(0, 4)));
    benchmarkMonthly.forEach((b) => b.rows.forEach((_, key) => set.add(key.slice(0, 4))));
    return Array.from(set).sort();
  }, [scenarioMonthly, benchmarkMonthly]);

  const [year, setYear] = useState(null);
  useEffect(() => {
    setYear((prev) => {
      if (!years.length) return null;
      return prev && years.includes(prev) ? prev : years[years.length - 1];
    });
  }, [years]);

  if (!active || !year) return null;

  const columns = [
    { id: active.id, label: active.label, rows: scenarioMonthly },
    ...benchmarkMonthly.map((b) => ({ id: b.id, label: b.label, rows: b.rows })),
  ];

  return (
    <div className="vector-capital-panel vcc-section-panel">
      <div className="vcc-section-header">
        <button
          type="button"
          className="vcc-section-toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <div>
            <span className="vector-capital-panel-eyebrow">Breakdown</span>
            <h3>Monthly Returns</h3>
          </div>
          <IconChevronDown
            size={18}
            color="rgba(191, 219, 254, 0.9)"
            className={expanded ? 'vcc-section-chevron is-open' : 'vcc-section-chevron'}
          />
        </button>
        <MonthlyReturnsInfoButton
          capitalBase={capitalBase}
          method={scenarioMonthlyMethod}
          className="vector-capital-metric-info-btn"
          iconColor="rgba(148, 163, 184, 0.95)"
          ariaLabel="Open monthly returns explanation"
        />
      </div>
      {expanded && (
        <>
          <div className="vcc-trades-toolbar">
            <span className="vcc-trades-title">Monthly Returns ({year})</span>
            <div className="vcc-toolbar">
              <ScenarioSelect scenarios={scenarios} active={active} onChange={setActiveId} />
              <select
                className="vcc-select"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                aria-label="Year"
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="vcc-table-scroll">
            <table className="vcc-table">
              <thead>
                <tr>
                  <th>Month</th>
                  {columns.map((c) => (
                    <th key={c.id} className="vcc-table-center">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MONTH_LABELS_SHORT.map((m, idx) => {
                  const key = `${year}-${String(idx + 1).padStart(2, '0')}`;
                  return (
                    <tr key={key}>
                      <td className="vcc-table-month">{m} {year}</td>
                      {columns.map((c) => {
                        const row = c.rows.get(key);
                        if (!row || row.pct === null) {
                          return <td key={c.id} className="vcc-table-num vcc-table-center muted">–</td>;
                        }
                        const big = Math.abs(row.pct) > 10;
                        const tone = pnlToneClass(row.pct);
                        return (
                          <td
                            key={c.id}
                            className={`vcc-table-num vcc-table-center ${tone} ${big && tone ? `vcc-cell-tint-${tone}` : ''}`}
                          >
                            {formatPct(row.pct)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function formatTradePrice(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(Number(value));
}

/** Simulated daily results table for one scenario + month (Fyntrak "List of Trades"). */
export function VectorCapitalTradesList({ scenarios }) {
  const [expanded, setExpanded] = useState(true);
  const [active, setActiveId] = useActiveScenario(scenarios);
  // 'daily' = simulated daily account values on the capital base (default);
  // 'trades' = per-trade journal rows. Toggle only shows when the scenario has
  // journal trades.
  const [viewMode, setViewMode] = useState('daily');

  const journalTrades = useMemo(
    () => (Array.isArray(active?.trades) && active.trades.length ? active.trades : null),
    [active],
  );
  const showJournal = Boolean(journalTrades) && viewMode === 'trades';
  const isPortfolioLegTrades = useMemo(() => {
    if (!journalTrades) return false;
    return new Set(journalTrades.map((t) => t.symbol).filter(Boolean)).size > 1;
  }, [journalTrades]);

  const dailyPnlByDate = useMemo(
    () => buildDailyPnlByDate(activeSeriesForDaily(active)),
    [active],
  );
  // Day → direction label from the journal trades: 'Long', 'Short', or
  // 'Long & Short' when the day mixed both. Null when no trade carries a side.
  const dailySideByDate = useMemo(() => {
    if (!journalTrades) return null;
    const map = new Map();
    journalTrades.forEach((t) => {
      const side = String(t.position || t.market_position || '').trim();
      if (!side) return;
      const date = String(t.trade_date).slice(0, 10);
      const set = map.get(date) || new Set();
      set.add(side);
      map.set(date, set);
    });
    if (!map.size) return null;
    const out = new Map();
    map.forEach((set, date) => {
      out.set(date, set.size > 1 ? 'Long & Short' : [...set][0]);
    });
    return out;
  }, [journalTrades]);
  const monthKeys = useMemo(() => {
    if (showJournal) {
      const set = new Set(journalTrades.map((t) => String(t.trade_date).slice(0, 7)));
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    }
    return monthKeysOf(dailyPnlByDate);
  }, [showJournal, journalTrades, dailyPnlByDate]);
  const [monthKey, setMonthKey] = useMonthKey(monthKeys);

  const rows = useMemo(() => {
    if (!monthKey) return [];
    if (showJournal) {
      return journalTrades
        .filter((t) => String(t.trade_date).startsWith(`${monthKey}-`))
        .map((t) => ({
          date: t.trade_date,
          symbol: t.symbol || active.symbol || '—',
          status: t.status || (Number(t.net_pnl) >= 0 ? 'Win' : 'Loss'),
          side: t.position || t.market_position || null,
          volume: Number(t.volume) || 0,
          entry: t.entry_price,
          exit: t.exit_price,
          pnl: Number(t.net_pnl),
          pct: Number(t.net_roi),
          isJournal: true,
        }))
        .sort((a, b) => b.date.localeCompare(a.date) || String(a.symbol).localeCompare(String(b.symbol)));
    }
    const out = [];
    dailyPnlByDate.forEach((data, date) => {
      if (!date.startsWith(`${monthKey}-`)) return;
      if (!data.pnl) return; // only days where the scenario actually moved
      out.push({ date, ...data, side: dailySideByDate?.get(date) || null, isJournal: false });
    });
    return out.sort((a, b) => b.date.localeCompare(a.date));
  }, [dailyPnlByDate, monthKey, showJournal, journalTrades, active, dailySideByDate]);

  if (!active || !monthKey) return null;

  const useJournalColumns = showJournal;
  // Daily Value gets a Side column only when the journal actually carries
  // direction data (pure equity-curve strategies would show all dashes).
  const showSideColumn = useJournalColumns || Boolean(dailySideByDate);
  // Equity Portfolios trade a basket of stocks, so the Daily Value rows have no
  // single meaningful symbol — drop the column there. Single-instrument
  // strategies (MMA → NQ, etc.) keep it.
  const hideSymbolColumn = !useJournalColumns && active.symbol === 'PORTFOLIO';

  return (
    <div className="vector-capital-panel vcc-section-panel">
      <button
        type="button"
        className="vcc-section-toggle"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <div>
          <span className="vector-capital-panel-eyebrow">Activity</span>
          <h3>List of Trades</h3>
        </div>
        <IconChevronDown
          size={18}
          color="rgba(191, 219, 254, 0.9)"
          className={expanded ? 'vcc-section-chevron is-open' : 'vcc-section-chevron'}
        />
      </button>
      {expanded && (
        <>
          <div className="vcc-trades-toolbar">
            <span className="vcc-trades-title">
              Results for {MONTH_LABELS[Number(monthKey.slice(5, 7)) - 1]} {monthKey.slice(0, 4)}
            </span>
            <div className="vcc-toolbar">
              {journalTrades ? (
                <div className="vcc-view-toggle" role="tablist" aria-label="List of Trades view">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === 'daily'}
                    className={viewMode === 'daily' ? 'active' : ''}
                    onClick={() => setViewMode('daily')}
                  >
                    Daily Value
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === 'trades'}
                    className={viewMode === 'trades' ? 'active' : ''}
                    onClick={() => setViewMode('trades')}
                  >
                    Trades
                  </button>
                </div>
              ) : null}
              <ScenarioSelect scenarios={scenarios} active={active} onChange={setActiveId} />
              <MonthNav monthKeys={monthKeys} monthKey={monthKey} onChange={setMonthKey} />
            </div>
          </div>
          {rows.length === 0 ? (
            <div className="vcc-empty">
              {useJournalColumns
                ? `No trades for this strategy in ${monthKeyLabel(monthKey)}.`
                : `No trading days for this strategy in ${monthKeyLabel(monthKey)}.`}
            </div>
          ) : (
            <div className="vcc-table-scroll">
              <table className="vcc-table vcc-trades-table">
                <thead>
                  <tr>
                    <th>Trade Date</th>
                    {!hideSymbolColumn && <th>Symbol</th>}
                    {showSideColumn && <th>Side</th>}
                    <th>Status</th>
                    <th>Volume</th>
                    <th>{useJournalColumns ? 'Entry Price' : 'Entry Value'}</th>
                    <th>{useJournalColumns ? 'Exit Price' : 'Exit Value'}</th>
                    <th>Net P&amp;L</th>
                    <th>Net ROI</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={useJournalColumns ? `${row.date}-${row.symbol}-${idx}` : row.date}>
                      <td>{tradeDateLabel(row.date)}</td>
                      {!hideSymbolColumn && (
                        <td className="vcc-trade-symbol">{row.symbol || active.symbol || '—'}</td>
                      )}
                      {showSideColumn && (
                        <td>
                          {row.side ? (
                            <span className={`vcc-trade-side ${row.side === 'Long & Short' ? 'mixed' : String(row.side).toLowerCase()}`}>
                              {row.side}
                            </span>
                          ) : '—'}
                        </td>
                      )}
                      <td>
                        <span className={`vcc-trade-status ${row.pnl > 0 ? 'win' : 'loss'}`}>
                          {row.status || (row.pnl > 0 ? 'Win' : 'Loss')}
                        </span>
                      </td>
                      <td className="vcc-table-num">{row.volume ?? (active.positions || 1)}</td>
                      <td className="vcc-table-num">
                        {row.isJournal ? formatTradePrice(row.entry) : formatAccountValue(row.entry)}
                      </td>
                      <td className="vcc-table-num">
                        {row.isJournal ? formatTradePrice(row.exit) : formatAccountValue(row.exit)}
                      </td>
                      <td className={`vcc-table-num ${pnlToneClass(row.pnl)}`}>{formatTradePnl(row.pnl)}</td>
                      <td className={`vcc-table-num ${pnlToneClass(row.pnl)}`}>{formatPct(row.pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(() => {
            // A scenario whose daily values come from somewhere other than these
            // trades (the OG portfolio mirrors Fyntrak's export) has to say so —
            // otherwise the two tables not tying out reads as a bug.
            const displayOnlyTrades = Boolean(active?.dailyStatsAuthoritative);
            const footnote = useJournalColumns
              ? (displayOnlyTrades
                ? 'Per-leg trades from Strategy Data. Daily values for this portfolio mirror the ingested Fyntrak curve, so historical trades here will not sum exactly to the Daily Value rows.'
                : (isPortfolioLegTrades
                  ? ''
                  : 'Completed trades from the strategy trade journal (TradesViz import). Entry/exit are contract prices; net P&L and ROI use the $100k fixed notional.'))
              : 'Simulated daily strategy results (one row per day the strategy moved). Symbol and volume reflect the strategy\'s composition; entry/exit are simulated account values — not actual trade executions.';
            return footnote ? (
              <p className="vector-capital-chart-footnote">{footnote}</p>
            ) : null;
          })()}
        </>
      )}
    </div>
  );
}

export default VectorCapitalCalendar;
